import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from '../common/Card';
import Button from '../common/Button';
import Spinner from '../common/Spinner';
import { alzheimerReportService, generateAlzheimerReportHTML } from '../../services/alzheimerReportService';
import { patientService } from '../../services/patientService';
import { format } from 'date-fns';
import { requestPDF } from '../../services/pdfService';
import PublishingOverlay from '../common/PublishingOverlay';
import { generatePDFFilename } from '../../utils/generatePDFFilename';

const AlzheimerReportForm = () => {
  const navigate = useNavigate();
  const { id, patientId } = useParams();
  const appointmentId = patientId;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [publishingStatus, setPublishingStatus] = useState(null);
  const [patient, setPatient] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);

  const [formData, setFormData] = useState({
    patient_id: '',
    patient_name: '',
    patient_gender: '',
    patient_age: '',
    exam_date: format(new Date(), 'yyyy-MM-dd'),
    
    // Alzheimer assessment fields
    moca: '',
    pTau_217: '',
    nfl: '',
    apoE4: '',
    
    pdf_url: ''
  });

  const sections = [
    {
      label: 'Patient Information',
      fields: [
        { name: 'patient_name', label: 'Patient Name', type: 'text', required: true },
        { name: 'patient_age', label: 'Age', type: 'number', required: true },
        { name: 'patient_gender', label: 'Gender', type: 'select', options: ['Male', 'Female', 'Other'], required: true },
        { name: 'exam_date', label: 'Exam Date', type: 'date', required: true }
      ]
    },
    {
      label: 'Alzheimer Assessment',
      component: 'alzheimer_assessment'
    }
  ];

  // Helper function to calculate age from DOB
  const calculateAgeFromDOB = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Helper function to normalize gender
  const normalizeGender = (gender) => {
    if (!gender) return '';
    const genderLower = gender.toLowerCase().trim();
    if (genderLower === 'male' || genderLower === 'm') return 'Male';
    if (genderLower === 'female' || genderLower === 'f') return 'Female';
    if (genderLower === 'other') return 'Other';
    return gender;
  };

  // Load patient data
  useEffect(() => {
    const loadPatientData = async () => {
      try {
        if (appointmentId) {
          console.log('Loading patient data for appointmentId:', appointmentId);
          const response = await patientService.getPatientById(appointmentId);
          console.log('Patient data response:', response);
          
          const patientData = response.data?.data || response.data || response;
          console.log('Extracted patient data:', patientData);
          
          setPatient(patientData);
          
          // Extract and process patient information
          const profileData = patientData.profile || {};
          const patientName = profileData.name || '';
          const patientGender = normalizeGender(profileData.gender);
          
          let patientPhone = patientData.user.phone;
          let patientAge = profileData.age;
          if (!patientAge && profileData.dob) {
            patientAge = calculateAgeFromDOB(profileData.dob);
          }
          
          setFormData(prev => ({
            ...prev,
            patient_id: patientData.id || '',
            patient_name: patientName,
            patient_gender: patientGender,
            patient_age: patientAge || '',
            patient_phone: patientPhone || ''
          }));

          // Generate and log filename on page load
          const fileName = generatePDFFilename(
            patientName,
            'alzheimer',
            patientData.sukra_order_id || patientData.id
          );
          console.log('📄 Generated PDF Filename:', fileName);
          console.log('Patient Name:', patientName);
          console.log('Order ID:', patientData.sukra_order_id || patientData.id);
          
          // Fetch existing alzheimer report data by appointment ID
          try {
            console.log('Attempting to fetch existing alzheimer report for appointmentId:', appointmentId);
            const reportData = await alzheimerReportService.getReportByAppointmentId(appointmentId);
            console.log('Report data response:', reportData);
            
            if (reportData && reportData.data) {
              setReportId(reportData.data.id);
              setIsEditMode(true);
              console.log('Existing report found, setting edit mode with reportId:', reportData.data.id);
              
              // Transform API response to form data structure
              const apiData = reportData.data;
              const transformedData = {
                patient_id: apiData.patient_id || '',
                patient_name: apiData.patient_name || '',
                patient_gender: apiData.patient_gender || '',
                patient_age: apiData.patient_age || '',
                exam_date: apiData.exam_date || '',
                patient_phone: patientPhone || '',
                // Convert string values from API to float for form display
                moca: apiData.moca ? parseFloat(apiData.moca) : '',
                pTau_217: apiData.pTau_217 ? parseFloat(apiData.pTau_217) : '',
                nfl: apiData.nfl ? parseFloat(apiData.nfl) : '',
                apoE4: apiData.apoE4 ? parseFloat(apiData.apoE4) : '',
                
                pdf_url: apiData.pdf_url || ''
              };
              
              setFormData(prev => ({
                ...prev,
                ...transformedData
              }));
            } else {
              console.log('No report data found in response');
              setIsEditMode(false);
            }
          } catch (reportError) {
            console.log('No existing alzheimer report found or error fetching:', reportError.message);
            setIsEditMode(false);
          }
        } else {
          console.log('No appointmentId provided');
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load patient data');
      } finally {
        setLoading(false);
      }
    };

    loadPatientData();
  }, [appointmentId]);

  const renderPatientInfoSection = () => {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Patient Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.patient_name || ''}
              onChange={(e) => handleFormChange('patient_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="Enter patient name"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Age <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.patient_age || ''}
              onChange={(e) => handleFormChange('patient_age', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="Enter age"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.patient_gender || ''}
              onChange={(e) => handleFormChange('patient_gender', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Exam Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.exam_date || ''}
              onChange={(e) => handleFormChange('exam_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>
        </div>
      </div>
    );
  };

  // Helper function to handle float input
  const handleFloatChange = (fieldName, value) => {
    // Allow empty string or valid float numbers
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      handleFormChange(fieldName, value ? parseFloat(value) : '');
    }
  };

  const renderAlzheimerAssessmentSection = () => {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              MoCA
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.moca || ''}
              onChange={(e) => handleFloatChange('moca', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="Enter MoCA score"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              pTau-217
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.pTau_217 || ''}
              onChange={(e) => handleFloatChange('pTau_217', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="Enter pTau-217 value"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              NfL
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.nfl || ''}
              onChange={(e) => handleFloatChange('nfl', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="Enter NfL value"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              ApoE4
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.apoE4 || ''}
              onChange={(e) => handleFloatChange('apoE4', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="Enter ApoE4 value"
            />
          </div>
        </div>
      </div>
    );
  };

  const handlePreview = async () => {
    try {
      const html = await generateAlzheimerReportHTML(formData);
      sessionStorage.setItem('alzheimerReportData', JSON.stringify(formData));
      
      const previewUrl = `/alzheimer-report/${appointmentId}/preview`;
      window.open(previewUrl, '_blank');
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to generate preview');
    }
  };

  const handleSubmit = async (status) => {
    setSubmitting(true);
    let pdf_url = null;
    
    try {
      if (status === 'published') {
        setPublishingStatus('generating');
        try {
          const html = await generateAlzheimerReportHTML(formData);
          const pdfBlob = await requestPDF(html);
          const pdfObjectUrl = URL.createObjectURL(pdfBlob);
          
          const patientName = patient?.profile?.name || formData.patient_name || patient?.name || 'patient';
          const orderId = patient?.sukra_order_id || patient?.id || 'unknown';
          const fileName = generatePDFFilename(patientName, 'alzheimer', orderId);
          
          console.log('🔍 PDF Generation Debug Info:');
          console.log('patient?.profile?.name:', patient?.profile?.name);
          console.log('formData.patient_name:', formData.patient_name);
          console.log('Final patientName used:', patientName);
          console.log('patient?.sukra_order_id:', patient?.sukra_order_id);
          console.log('patient?.id:', patient?.id);
          console.log('Final orderId used:', orderId);
          console.log('📄 Final PDF Filename:', fileName);
          
          try {
            const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
            const uploadFormData = new FormData();
            uploadFormData.append('file', pdfFile);
            uploadFormData.append('file_name', fileName);
            
            const token = localStorage.getItem('token');
            
            const uploadRes = await fetch('https://vitaldev.vitalinsights.in/user/upload-file-to-s3', {
              method: 'POST',
              body: uploadFormData,
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (!uploadRes.ok) {
              const errorText = await uploadRes.text();
              console.error('Upload error response:', errorText);
              throw new Error(`Upload failed with status ${uploadRes.status}: ${errorText}`);
            }
            
            const response = await uploadRes.json();
            const fileUrl = response.fileUrl || response.file_url || response.url || response.data?.fileUrl || response.data?.file_url;
            
            if (!fileUrl) {
              console.error('No file URL found in response. Full response:', response);
              throw new Error('Invalid response from server - no file URL');
            }
            
            pdf_url = String(fileUrl);
            console.log('Successfully set PDF URL:', pdf_url);
          } catch (uploadError) {
            console.error('Error during PDF upload:', uploadError);
            pdf_url = pdfObjectUrl;
            console.warn('Using object URL as fallback:', pdf_url);
          }
          
          console.log('PDF generated and uploaded:', pdf_url);
        } catch (error) {
          console.error('Error generating PDF:', error);
          setPublishingStatus('error');
          toast.error('Failed to generate PDF');
          setSubmitting(false);
          return;
        }
      }
      
      // Convert float values to strings for API submission
      const payload = {
        ...formData,
        // Convert float biomarker values to strings
        moca: formData.moca !== '' ? String(formData.moca) : '',
        pTau_217: formData.pTau_217 !== '' ? String(formData.pTau_217) : '',
        nfl: formData.nfl !== '' ? String(formData.nfl) : '',
        apoE4: formData.apoE4 !== '' ? String(formData.apoE4) : '',
        report_status: status,
        appointment_id: appointmentId
      };
      
      if (pdf_url) {
        console.log('Setting PDF URL in submission:', pdf_url);
        payload.pdf_url = pdf_url;
      }

      console.log('About to submit with payload:', JSON.stringify(payload));
      
      const response = await alzheimerReportService.createReport(payload);
      console.log('Create report response:', response);
      toast.success('Report submitted successfully');

      if (status === 'published') {
        setPublishingStatus('success');
        setTimeout(() => {
          setPublishingStatus(null);
          setSubmitting(false);
          navigate('/patients');
        }, 2000);
      } else {
        setSubmitting(false);
        navigate('/patients');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit report');
      setSubmitting(false);
      if (status === 'published') {
        setPublishingStatus('error');
      }
    }
  };

  const handleCancel = () => {
    navigate('/patients');
  };

  const goToNextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const goToPreviousSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderField = (field) => {
    const value = formData[field.name];

    if (field.type === 'textarea') {
      return (
        <div key={field.name} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <textarea
            value={value || ''}
            onChange={(e) => handleFormChange(field.name, e.target.value)}
            className="w-full p-2 border rounded"
            rows="4"
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.name} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <select
            value={value || ''}
            onChange={(e) => handleFormChange(field.name, e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="">Select {field.label}</option>
            {field.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div key={field.name} className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {field.label} {field.required && <span className="text-red-500">*</span>}
        </label>
        <input
          type={field.type}
          value={value || ''}
          onChange={(e) => handleFormChange(field.name, e.target.value)}
          className="w-full p-2 border rounded"
          placeholder={`Enter ${field.label.toLowerCase()}`}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      {publishingStatus && (
        <PublishingOverlay status={publishingStatus} />
      )}
      
      <div className="flex flex-col md:flex-row gap-4 p-4">
        <div className="md:w-64 flex-shrink-0">
          <div className="bg-white p-4 rounded-lg shadow space-y-6">
            {/* Patient Information */}
            {patient && (
              <div className="border-b pb-4">
                <h3 className="text-lg font-bold text-gray-800 mb-3">{patient.profile?.name}</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">ID:</span>
                    <span className="font-medium">{patient.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Branch:</span>
                    <span className="font-medium">{patient.sukra_branch_id}</span>
                  </div>
                  {patient.slot_start_time && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Appointment:</span>
                      <span className="font-medium">{patient.slot_start_time?.split("T")[0]}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Report Sections */}
            <div>
              <h3 className="text-xl font-bold text-orange-500 mb-4">Report Sections</h3>
              <ul className="space-y-2">
                {sections.map((section, index) => (
                  <li key={index}>
                    <button
                      onClick={() => setCurrentSection(index)}
                      disabled={submitting}
                      className={`w-full text-left py-2 px-3 rounded transition ${currentSection === index 
                        ? 'bg-orange-500 text-white font-medium' 
                        : 'hover:bg-gray-100'}`}
                    >
                      {section.label}
                      {currentSection === index && (
                        <span className="float-right">→</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        
        <div className="flex-1">
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold">
                {isEditMode ? 'Edit Alzheimer Report' : 'New Alzheimer Report'}
              </h1>
            </div>
            
            <div className="mb-4">
              <h2 className="text-xl font-semibold">
                {sections[currentSection].label}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Complete all fields in this section before proceeding
              </p>
            </div>

            <div className="space-y-6">
              {sections[currentSection].component === 'alzheimer_assessment' ? (
                renderAlzheimerAssessmentSection()
              ) : (
                sections[currentSection].fields.map(field => renderField(field))
              )}
            </div>

            <div className="flex justify-between mt-6">
              <Button
                variant="secondary"
                onClick={currentSection > 0 ? goToPreviousSection : handleCancel}
              >
                {currentSection > 0 ? 'Previous' : 'Back to List'}
              </Button>

              {currentSection === sections.length - 1 ? (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={handlePreview}
                    disabled={submitting}
                  >
                    Preview Report
                  </Button>
                  <Button
                    onClick={() => handleSubmit('submitted')}
                    disabled={submitting}
                    variant="secondary"
                  >
                    {submitting ? 'Saving...' : 'Submit Report'}
                  </Button>
                  <Button
                    onClick={() => handleSubmit('published')}
                    disabled={submitting}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    {submitting ? 'Publishing...' : 'Submit & Publish'}
                  </Button>
                </div>
              ) : (
                <Button onClick={goToNextSection}>Next</Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default AlzheimerReportForm;
